const AUDIO_SINK_OBJECT_NAME = "invisible-avatar-voice-sink";
const AVATAR_AUDIO_SOURCE_TYPE = 1;
const SYNC_INTERVAL_MS = 5000;

let voiceSinkManager = null;

function getGlobalApp() {
  if (typeof window !== "undefined" && window.APP) {
    return window.APP;
  }

  if (typeof global !== "undefined" && global.APP) {
    return global.APP;
  }

  return null;
}

function getGlobalScene() {
  if (typeof AFRAME !== "undefined") {
    return AFRAME.scenes?.[0] || null;
  }

  return null;
}

function getGlobalThree() {
  if (typeof THREE !== "undefined") {
    return THREE;
  }

  if (typeof window !== "undefined" && window.THREE) {
    return window.THREE;
  }

  return null;
}

function getMediaStreamConstructor() {
  if (typeof MediaStream !== "undefined") {
    return MediaStream;
  }

  if (typeof window !== "undefined" && window.MediaStream) {
    return window.MediaStream;
  }

  return null;
}

function normalizePeerId(peerId) {
  return peerId === undefined || peerId === null ? null : String(peerId);
}

function isActiveAudioTrack(track) {
  return Boolean(track && track.kind === "audio" && track.readyState !== "ended");
}

function getAudioTrackFromStream(stream) {
  return stream?.getAudioTracks?.()[0] || stream?.getTracks?.().find(track => track.kind === "audio") || null;
}

function getLocalClientId(app, explicitLocalClientId) {
  return normalizePeerId(
    explicitLocalClientId ||
      app?.dialog?._clientId ||
      (typeof NAF !== "undefined" && NAF.clientId) ||
      (typeof window !== "undefined" && window.NAF?.clientId)
  );
}

function collectMediasoupAudioPeerIds(dialog, localClientId, peerIds) {
  dialog?._consumers?.forEach?.(consumer => {
    const peerId = normalizePeerId(consumer?.appData?.peerId);

    if (!peerId || peerId === localClientId || consumer?.closed || !isActiveAudioTrack(consumer?.track)) {
      return;
    }

    peerIds.add(peerId);
  });
}

function collectAgoraAudioPeerIds(dialog, localClientId, peerIds) {
  const audioPublishers = dialog?._audioPublishers;

  if (!audioPublishers) {
    return;
  }

  Object.keys(audioPublishers).forEach(peerId => {
    const normalizedPeerId = normalizePeerId(peerId);

    if (normalizedPeerId && normalizedPeerId !== localClientId) {
      peerIds.add(normalizedPeerId);
    }
  });
}

function getRemoteAudioPeerIds(app, localClientId) {
  const peerIds = new Set();
  const dialog = app?.dialog;

  collectMediasoupAudioPeerIds(dialog, localClientId, peerIds);
  collectAgoraAudioPeerIds(dialog, localClientId, peerIds);

  return peerIds;
}

function defaultCreateSinkElement(scene, peerId) {
  if (typeof document === "undefined" || !document.createElement) {
    return null;
  }

  const el = document.createElement("a-entity");
  el.classList?.add?.("invisible-avatar-voice-sink");
  el.setAttribute?.("data-peer-id", peerId);
  if (el.object3D) {
    el.object3D.visible = false;
  }
  scene?.appendChild?.(el);
  return el;
}

function shouldCreateSilentAudioEl(navigatorObject = typeof navigator !== "undefined" ? navigator : null) {
  return /chrome/i.test(navigatorObject?.userAgent || "");
}

function createSilentAudioEl(stream, audioConstructor = typeof Audio !== "undefined" ? Audio : null) {
  if (!audioConstructor) {
    return null;
  }

  const audioEl = new audioConstructor();
  audioEl.setAttribute?.("autoplay", "autoplay");
  audioEl.setAttribute?.("playsinline", "playsinline");
  audioEl.srcObject = stream;
  audioEl.volume = 0;
  return audioEl;
}

function resolveDeps(deps = {}) {
  const app = deps.app || getGlobalApp();
  const scene = deps.scene || app?.scene || getGlobalScene();

  return {
    app,
    scene,
    three: deps.three || getGlobalThree(),
    mediaStream: deps.mediaStream || getMediaStreamConstructor(),
    createSinkElement: deps.createSinkElement || defaultCreateSinkElement,
    setInterval: deps.setInterval || (typeof window !== "undefined" ? window.setInterval.bind(window) : setInterval),
    clearInterval:
      deps.clearInterval || (typeof window !== "undefined" ? window.clearInterval.bind(window) : clearInterval),
    sourceType: deps.sourceType || AVATAR_AUDIO_SOURCE_TYPE,
    updateAudioSettings: deps.updateAudioSettings || null,
    audioConstructor: deps.audioConstructor || (typeof Audio !== "undefined" ? Audio : null),
    navigator: deps.navigator || (typeof navigator !== "undefined" ? navigator : null),
    localClientId: deps.localClientId
  };
}

class InvisibleAvatarVoiceSinkManager {
  constructor(deps = {}) {
    this.deps = resolveDeps(deps);
    this.sinks = new Map();
    this.pendingPeerIds = new Set();
    this.enabled = false;
    this.syncInterval = null;
    this.onStreamUpdated = this.onStreamUpdated.bind(this);
    this.sync = this.sync.bind(this);
  }

  updateDeps(deps = {}) {
    this.deps = { ...this.deps, ...resolveDeps(deps) };
  }

  async start(deps = {}) {
    this.updateDeps(deps);

    if (!this.enabled) {
      this.enabled = true;
      this.deps.app?.dialog?.on?.("stream_updated", this.onStreamUpdated);
      this.syncInterval = this.deps.setInterval(this.sync, SYNC_INTERVAL_MS);
    }

    await this.sync();
  }

  stop() {
    this.enabled = false;
    this.deps.app?.dialog?.off?.("stream_updated", this.onStreamUpdated);

    if (this.syncInterval) {
      this.deps.clearInterval(this.syncInterval);
      this.syncInterval = null;
    }

    this.pendingPeerIds.clear();
    for (const peerId of Array.from(this.sinks.keys())) {
      this.disposeSink(peerId);
    }
  }

  onStreamUpdated(peerId, kind) {
    if (kind !== "audio") {
      return;
    }

    const normalizedPeerId = normalizePeerId(peerId);

    if (normalizedPeerId) {
      this.disposeSink(normalizedPeerId);
    }

    this.sync();
  }

  async sync() {
    if (!this.enabled) {
      return;
    }

    const { app, localClientId } = this.deps;
    const activePeerIds = getRemoteAudioPeerIds(app, getLocalClientId(app, localClientId));

    for (const peerId of Array.from(this.sinks.keys())) {
      if (!activePeerIds.has(peerId)) {
        this.disposeSink(peerId);
      }
    }

    await Promise.all(
      Array.from(activePeerIds).map(peerId => {
        if (this.sinks.has(peerId) || this.pendingPeerIds.has(peerId)) {
          return Promise.resolve();
        }

        return this.createSink(peerId);
      })
    );
  }

  async createSink(peerId) {
    const { app, mediaStream: MediaStreamConstructor } = this.deps;

    if (!app?.dialog?.getMediaStream || !MediaStreamConstructor) {
      return;
    }

    this.pendingPeerIds.add(peerId);

    let stream;
    try {
      stream = await app.dialog.getMediaStream(peerId, "audio");
    } catch (error) {
      console.error(`Failed to create invisible avatar voice sink for ${peerId}`, error);
    } finally {
      this.pendingPeerIds.delete(peerId);
    }

    if (!this.enabled || this.sinks.has(peerId) || !stream) {
      return;
    }

    const track = getAudioTrackFromStream(stream);
    if (!isActiveAudioTrack(track)) {
      return;
    }

    const sink = this.createAudioSink(peerId, stream, track);

    if (sink) {
      this.sinks.set(peerId, sink);
    }
  }

  createAudioSink(peerId, stream, track) {
    const { app, scene, three, createSinkElement, audioConstructor, navigator: navigatorObject } = this.deps;
    const audioSystem = scene?.systems?.["hubs-systems"]?.audioSystem;
    const audioListener = scene?.audioListener;

    if (!audioSystem?.addAudio || !three?.Audio || !audioListener) {
      return null;
    }

    const el = createSinkElement(scene, peerId);
    if (!el) {
      return null;
    }
    if (!el.parentNode) {
      scene?.appendChild?.(el);
    }

    const audio = new three.Audio(audioListener);
    const mediaStreamSource = audio.context.createMediaStreamSource(stream);
    audio.setNodeSource(mediaStreamSource);
    el.setObject3D?.(AUDIO_SINK_OBJECT_NAME, audio);

    const silentAudioEl = shouldCreateSilentAudioEl(navigatorObject)
      ? createSilentAudioEl(stream, audioConstructor)
      : null;

    audioSystem.addAudio({ sourceType: this.deps.sourceType, node: audio });
    app?.sourceType?.set?.(el, this.deps.sourceType);
    app?.audios?.set?.(el, audio);
    this.deps.updateAudioSettings?.(el, audio);

    const onTrackEnded = () => {
      this.disposeSink(peerId);
      this.sync();
    };
    track.addEventListener?.("ended", onTrackEnded);

    return {
      audio,
      audioSystem,
      el,
      mediaStreamSource,
      onTrackEnded,
      silentAudioEl,
      track
    };
  }

  disposeSink(peerId) {
    const sink = this.sinks.get(peerId);

    if (!sink) {
      return;
    }

    sink.track?.removeEventListener?.("ended", sink.onTrackEnded);

    try {
      sink.mediaStreamSource?.disconnect?.();
    } catch (error) {
      // Disconnection can throw if the node was already disconnected.
    }

    sink.audioSystem?.removeAudio?.({ node: sink.audio });
    this.deps.app?.audios?.delete?.(sink.el);
    this.deps.app?.sourceType?.delete?.(sink.el);
    sink.el?.removeObject3D?.(AUDIO_SINK_OBJECT_NAME);
    sink.el?.parentNode?.removeChild?.(sink.el);

    if (sink.silentAudioEl) {
      sink.silentAudioEl.pause?.();
      sink.silentAudioEl.srcObject = null;
    }

    this.sinks.delete(peerId);
  }
}

export async function setInvisibleAvatarVoiceSinksEnabled(enabled, deps = {}) {
  if (!enabled) {
    voiceSinkManager?.stop();
    voiceSinkManager = null;
    return;
  }

  if (!voiceSinkManager) {
    voiceSinkManager = new InvisibleAvatarVoiceSinkManager(deps);
  }

  await voiceSinkManager.start(deps);
}

export async function syncInvisibleAvatarVoiceSinks(deps = {}) {
  if (!voiceSinkManager) {
    return;
  }

  voiceSinkManager.updateDeps(deps);
  await voiceSinkManager.sync();
}

export function resetInvisibleAvatarVoiceSinksForTests() {
  voiceSinkManager?.stop();
  voiceSinkManager = null;
}
